import type { AgeCategory, ClientDTO, ClientType, Language } from '@brainkey/shared';
import { Link2, Search, UserRound } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { errorMessage, fullName, normalizePhone } from '../lib/format';
import { useClientsList, useCreateClient } from '../lib/hooks/useClients';
import { useDebouncedValue } from '../lib/hooks/useDebouncedValue';
import { cn } from '../lib/utils';

import { Alert } from './ui/Alert';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { ClientCodeField } from './ui/ClientCodeField';
import { FieldChrome } from './ui/FieldChrome';
import { baseControlClasses, Input } from './ui/Input';
import { PhoneNumberField } from './ui/PhoneNumberField';
import { Select } from './ui/Select';

interface RelatedClientPickerProps {
  /** Currently linked client, if any — drives the collapsed "linked" state. */
  relatedClientId: string | null;
  /** Display name for the linked client (already hydrated onto the contact's `name`). */
  linkedName: string;
  /** Excludes this client from search results — the client whose relation is being edited can't link to themselves. */
  excludeClientId?: string | undefined;
  onLink: (client: ClientDTO) => void;
  onUnlink: () => void;
}

const EMPTY_NEW_CLIENT = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  languagePref: 'EN' as Language,
  ageCategory: '' as AgeCategory | '',
  clientType: '' as ClientType | '',
  clientCode: '',
};

/**
 * Search-or-create picker for linking a "Family & Relations" contact to a
 * real Client — e.g. finding a mother who's already registered elsewhere in
 * the system, or creating her on the spot, instead of only typing free text.
 * Once linked, the contact's name/phone/email/language stay live-synced
 * server-side (ClientContactService.hydrate) rather than a stale snapshot.
 */
export function RelatedClientPicker({
  relatedClientId,
  linkedName,
  excludeClientId,
  onLink,
  onUnlink,
}: RelatedClientPickerProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState(EMPTY_NEW_CLIENT);
  const [createError, setCreateError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounced = useDebouncedValue(query.trim(), 300);
  const createClient = useCreateClient();

  const results = useClientsList({
    ...(debounced !== '' ? { q: debounced } : {}),
    page: 1,
    pageSize: 8,
  });
  const matches = (results.data?.items ?? []).filter((c) => c.id !== excludeClientId);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const startCreate = (): void => {
    const [first, ...rest] = debounced.split(' ');
    setNewClient({ ...EMPTY_NEW_CLIENT, firstName: first ?? '', lastName: rest.join(' ') });
    setCreateError(null);
    setCreating(true);
  };

  const cancelCreate = (): void => {
    setCreating(false);
    setNewClient(EMPTY_NEW_CLIENT);
    setCreateError(null);
  };

  const submitCreate = async (): Promise<void> => {
    if (newClient.firstName.trim() === '' || newClient.lastName.trim() === '') {
      setCreateError('First and last name are required.');
      return;
    }
    if (newClient.clientType === '' || newClient.clientCode.trim() === '') {
      setCreateError('Please choose a client type and enter a client ID.');
      return;
    }
    setCreateError(null);
    try {
      const client = await createClient.mutateAsync({
        firstName: newClient.firstName.trim(),
        lastName: newClient.lastName.trim(),
        ...(newClient.email.trim() !== '' ? { email: newClient.email.trim() } : {}),
        ...(newClient.phone.trim() !== '' ? { phone: normalizePhone(newClient.phone) } : {}),
        languagePref: newClient.languagePref,
        ...(newClient.ageCategory !== '' ? { ageCategory: newClient.ageCategory } : {}),
        clientType: newClient.clientType,
        clientCode: newClient.clientCode.trim(),
      });
      onLink(client);
      setCreating(false);
      setNewClient(EMPTY_NEW_CLIENT);
      setQuery('');
      setOpen(false);
    } catch (err) {
      setCreateError(errorMessage(err, 'Could not create the client.'));
    }
  };

  if (relatedClientId !== null) {
    return (
      <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-brand-50/60 border border-brand-100 dark:bg-brand-500/10 dark:border-brand-500/20">
        <div className="flex items-center gap-2 min-w-0">
          <Link2 className="w-3.5 h-3.5 text-brand-500 dark:text-brand-300 shrink-0" />
          <Badge tone="accent">Linked account</Badge>
          <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
            {linkedName}
          </span>
        </div>
        <button
          type="button"
          onClick={onUnlink}
          className="shrink-0 text-xs font-medium text-zinc-500 hover:text-rose-600 dark:text-zinc-400 dark:hover:text-rose-400 transition-colors"
        >
          Unlink
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <FieldChrome
        label="Link to an existing client"
        hint="Search if this relation is already registered — or leave blank for a free-text contact."
        optional
        id="related-client-search"
      >
        <div className="relative" ref={containerRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 dark:text-zinc-500" />
            <input
              id="related-client-search"
              value={query}
              placeholder="Search by name, email, or phone…"
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setOpen(true)}
              autoComplete="off"
              className={cn(baseControlClasses, 'pl-9')}
            />
          </div>

          {open && debounced !== '' && !creating && (
            <div className="absolute z-[1100] mt-1.5 w-full overflow-hidden rounded-2xl border border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-zinc-900 shadow-soft dark:shadow-glass-dark">
              <div className="p-1.5 max-h-64 overflow-y-auto">
                {results.isFetching && matches.length === 0 && (
                  <div className="px-3 py-2 text-sm text-zinc-400 dark:text-zinc-500">
                    Searching…
                  </div>
                )}
                {matches.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      onLink(c);
                      setQuery('');
                      setOpen(false);
                    }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors duration-150 hover:bg-brand-50 dark:hover:bg-white/5"
                  >
                    <UserRound className="w-3.5 h-3.5 text-zinc-400 dark:text-zinc-500 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-zinc-800 dark:text-zinc-100 truncate">
                        {fullName(c)}
                      </div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                        {c.email ?? c.phone ?? 'No contact info'}
                      </div>
                    </div>
                  </button>
                ))}
                {!results.isFetching && matches.length === 0 && (
                  <div className="px-3 py-2 text-sm text-zinc-400 dark:text-zinc-500">
                    No matching clients.
                  </div>
                )}
                <div
                  className={cn(
                    'mt-1',
                    (matches.length > 0 || results.isFetching) &&
                      'border-t border-zinc-200 dark:border-white/10 pt-1.5',
                  )}
                >
                  <button
                    type="button"
                    onClick={startCreate}
                    className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium text-brand-600 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-white/5 transition-colors"
                  >
                    + Create &ldquo;{debounced}&rdquo; as a new client
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </FieldChrome>

      {creating && (
        <div className="flex flex-col gap-3 p-4 rounded-2xl bg-zinc-50 border border-zinc-100 dark:bg-zinc-900/40 dark:border-white/5">
          {createError !== null && <Alert tone="danger">{createError}</Alert>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="First name"
              value={newClient.firstName}
              onChange={(e) => setNewClient((p) => ({ ...p, firstName: e.target.value }))}
            />
            <Input
              label="Last name"
              value={newClient.lastName}
              onChange={(e) => setNewClient((p) => ({ ...p, lastName: e.target.value }))}
            />
            <PhoneNumberField
              label="Phone"
              optional
              value={newClient.phone}
              onChange={(v) => setNewClient((p) => ({ ...p, phone: v }))}
            />
            <Input
              label="Email"
              type="email"
              optional
              value={newClient.email}
              onChange={(e) => setNewClient((p) => ({ ...p, email: e.target.value }))}
            />
            <Select
              label="Preferred language"
              value={newClient.languagePref}
              onValueChange={(v) => setNewClient((p) => ({ ...p, languagePref: v as Language }))}
              options={[
                { value: 'EN', label: 'English' },
                { value: 'SI', label: 'Sinhala' },
                { value: 'TA', label: 'Tamil' },
              ]}
            />
            <Select
              label="Client type"
              value={newClient.clientType}
              onValueChange={(v) =>
                setNewClient((p) => ({ ...p, clientType: v as ClientType | '' }))
              }
              options={[
                { value: '', label: 'Select a client type…' },
                { value: 'LOCAL', label: 'Local' },
                { value: 'FOREIGN', label: 'Foreign' },
              ]}
            />
            <ClientCodeField
              label="Client ID"
              value={newClient.clientCode}
              onChange={(v) => setNewClient((p) => ({ ...p, clientCode: v }))}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={cancelCreate}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              loading={createClient.isPending}
              onClick={() => void submitCreate()}
            >
              Create &amp; link
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
